import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { Header } from './layout/Header';
import { Sidebar } from './layout/Sidebar';
import { Footer } from './layout/Footer';
import { HomeSkeleton } from './home/HomeSkeleton';

/** Context shared with pages that support a fullscreen mode (Logs, Search). */
export interface LayoutContext {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
}

const FULLSCREEN_ROUTES = ['/logs', '/search'];
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

export const Layout: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { currentEnvironment } = useEnvironment();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isLoginPage = location.pathname === '/login';
  const supportsFullscreen = FULLSCREEN_ROUTES.includes(location.pathname);
  // Keep the shell (sidebar/header) mounted while auth initializes — it's static
  // navigation and doesn't depend on any fetched data. ProtectedRoute renders a
  // content skeleton during that window.
  const showChrome = (isAuthenticated || isLoading) && !isLoginPage;
  const hideChrome = supportsFullscreen && isFullscreen;
  const suspenseFallback = <HomeSkeleton />;

  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((v) => {
      const next = !v;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  // Leave fullscreen when navigating to a route that doesn't support it.
  useEffect(() => {
    if (!supportsFullscreen) setIsFullscreen(false);
  }, [supportsFullscreen]);

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const outletContext: LayoutContext = { isFullscreen, toggleFullscreen };

  // Login page (and unauthenticated) renders standalone without chrome.
  if (!showChrome) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Outlet context={outletContext} />
      </div>
    );
  }

  // Fullscreen mode (Logs/Search): drop the shell entirely.
  if (hideChrome) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Suspense fallback={suspenseFallback}>
          <Outlet key={currentEnvironment} context={outletContext} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Header onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main className="flex-1 overflow-y-auto flex flex-col">
          <div className="flex-1 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Keyed on the environment so switching envs remounts only the page
                content (skeleton + re-fetch) while the shell stays put. The inner
                Suspense keeps the sidebar/header mounted while a lazy page chunk
                loads instead of tearing down the whole app. */}
            <Suspense fallback={suspenseFallback}>
              <Outlet key={currentEnvironment} context={outletContext} />
            </Suspense>
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Layout;

